import {
    SUBSCRIBE_MEMBER_CHECKOUT_REQUEST,
    SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS,
    SUBSCRIBE_MEMBER_CHECKOUT_FAILURE,
    SUBSCRIBE_CLUB_CHECKOUT_REQUEST,
    SUBSCRIBE_CLUB_CHECKOUT_SUCCESS,
    SUBSCRIBE_CLUB_CHECKOUT_FAILURE,
    SUBSCRIBE_MEMBER_CANCEL_REQUEST,
    SUBSCRIBE_MEMBER_CANCEL_SUCCESS,
    SUBSCRIBE_MEMBER_CANCEL_FAILURE,
    SUBSCRIBE_MEMBER_RENEW_REQUEST,
    SUBSCRIBE_MEMBER_RENEW_SUCCESS,
    SUBSCRIBE_MEMBER_RENEW_FAILURE,
    SUBSCRIBE_CLUB_RENEW_REQUEST,
    SUBSCRIBE_CLUB_RENEW_SUCCESS,
    SUBSCRIBE_CLUB_RENEW_FAILURE,
    SUBSCRIBE_UPDATE_PAYMENT_REQUEST,
    SUBSCRIBE_UPDATE_PAYMENT_SUCCESS,
    SUBSCRIBE_UPDATE_PAYMENT_FAILURE,
    SUBSCRIBE_SUBSCRIPTION_INFO_REQUEST,
    SUBSCRIBE_SUBSCRIPTION_INFO_SUCCESS,
    SUBSCRIBE_SUBSCRIPTION_INFO_FAILURE,
    SUBSCRIBE_INVOICE_PRINT,
    SUBSCRIBE_MEMBER_CLEAR_ERROR,
} from './subscribe-action-types';

import { FETCH_AUTHENTICATED_USER }
    from './index';

import {
    memberCheckout,
    powerMemberCheckout,
    confirmPowerMemberCheckout,
    deletePendingPowerSubscription,
    clubCheckout,
    confirmClubCheckout,
    failClubCheckout,
    updatePayment,
    subscriptionInfo,
    memberCancel,
    memberRenew,
    clubRenew,
    enterpriseClubCheckout,
    confirmEnterpriseClubCheckout,
    failEnterpriseClubCheckout,
    postMonthlyToAnnualUpgrade,
    confirmMonthlyToAnnualUpgrade,
    deleteFailedMonthlyToAnnualUpgrade,
} from '../api/checkoutApi';

import { confirm3dsPayment } from '../utils/stripePayments';
import { gtag } from '../components/Analytics/gtag';
import paymentsConstants from '../../constants/payments';

const ALREADY_SUBSCRIBED_PATTERN = /already (has|have)( a)? subscription|already subscribed|existing subscription/i;

const isExistingSubscriptionError = (err) => {
    if (err?.response?.status === 409) {
        return true;
    }
    const data = err?.response?.data;
    const rawMessage = data?.DisplayMessage ||
        data?.displayMessage ||
        data?.message ||
        data?.Message;
    return !!(rawMessage && ALREADY_SUBSCRIBED_PATTERN.test(rawMessage));
};

const extractFriendlyMessage = (data) => data?.DisplayMessage ||
    data?.displayMessage ||
    data?.message ||
    data?.Message;

const composeCheckoutErrorMessage = (defaultMessage, friendlyMessage) => {
    if (friendlyMessage) {
        return `${defaultMessage}: ${friendlyMessage}`;
    }
    return defaultMessage;
};

const getCheckoutErrorMessage = (err, defaultMessage) => {
    if (isExistingSubscriptionError(err)) {
        return paymentsConstants.paymentErrors.existing_subscription;
    }
    return composeCheckoutErrorMessage(defaultMessage, extractFriendlyMessage(err?.response?.data));
};

export const submitMemberCheckout = (values) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_REQUEST });
        const res = await memberCheckout(mainApi, values);
        const { data } = res;
        if (data.error) {
            gtag('event', {
                category: 'Power Upgrade',
                context: 'Power Upgrade Unsuccessful',
            });
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data });
            throw new Error(data.error);
        }
        dispatch({ type: FETCH_AUTHENTICATED_USER, payload: res });
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS });
        return data;
    } catch (err) {
        gtag('event', {
            category: 'Power Upgrade',
            context: 'Power Upgrade Unsuccessful',
        });
        const errText = getCheckoutErrorMessage(err, paymentsConstants.paymentErrors.checkout_fail);
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: errText } });
        throw err;
    }
};

export const submitMemberPowerCheckout = ({ values, onBinCountryMismatch, stripe }) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_REQUEST });
        const res = await powerMemberCheckout(mainApi, values);
        const { data } = res;
        let isMemberSubscribed = data?.isSubscribed;
        let currentMember = data?.currentMember;

        if (!isMemberSubscribed && data.paymentIntentClientSecret) {
            const failure = await confirm3dsPayment(stripe, data.paymentIntentClientSecret);
            if (failure) {
                await deletePendingPowerSubscription(mainApi, data?.sourceSubscriptionId);
                dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: failure.errorMessage } });
                return;
            }
            const confirmedMember = await confirmPowerMemberCheckout(mainApi, data.sourceSubscriptionId);
            if (!currentMember && confirmedMember?.data) {
                currentMember = confirmedMember?.data;
            }
            isMemberSubscribed = true;
        }

        // Check for BIN country mismatch
        if (!isMemberSubscribed && data.availablePowerPlans) {
            // Handle BIN country mismatch
            if (onBinCountryMismatch) {
                onBinCountryMismatch(data.availablePowerPlans, 'We have changed the billing details to match your billing country, please try again.');
            }
            // Don't treat this as an error - just return the data
            // needed to make sure the button is enabled after the BIN country mismatch
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS });
            return data;
        }

        if (data.error) {
            gtag('event', {
                category: 'Power Upgrade',
                context: 'Power Upgrade Unsuccessful',
            });
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data });
            throw new Error(data.error);
        }

        if (!isMemberSubscribed) {
            gtag('event', {
                category: 'Power Upgrade',
                context: 'Power Upgrade Unsuccessful',
            });
            const friendlyMessage = extractFriendlyMessage(data);
            const errorMessage = friendlyMessage
                ? composeCheckoutErrorMessage(paymentsConstants.paymentErrors.checkout_fail, friendlyMessage)
                : paymentsConstants.paymentErrors.checkout_fail_no_response;
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: errorMessage } });
            throw new Error(errorMessage);
        }

        dispatch({ type: FETCH_AUTHENTICATED_USER, payload: { data: { ...currentMember } } });
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS });
        return data;
    } catch (err) {
        gtag('event', {
            category: 'Power Upgrade',
            context: 'Power Upgrade Unsuccessful',
        });
        const errText = getCheckoutErrorMessage(err, paymentsConstants.paymentErrors.checkout_fail);
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: errText } });
        throw err;
    }
};

export const submitMonthlyToAnnualUpgrade = ({ values, onBinCountryMismatch, stripe }) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_REQUEST });
        const res = await postMonthlyToAnnualUpgrade(mainApi, values);
        const { data } = res;
        const isMemberSubscribed = data?.isSubscribed;
        let currentMember = data?.currentMember;
        const planData = data?.annualPlan;
        const amountPaid = planData?.amountPayPerRecurringInterval || data?.amountPaid;
        const currency = planData?.currency || data?.currency || 'USD';

        // Scenario 3: 3DS Required
        if (!isMemberSubscribed && data.paymentIntentClientSecret) {
            const failure = await confirm3dsPayment(stripe, data.paymentIntentClientSecret);
            if (failure) {
                await deleteFailedMonthlyToAnnualUpgrade(mainApi, data?.sourceSubscriptionId);
                dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: failure.errorMessage } });
                return { isSubscribed: false, error: failure.errorMessage };
            }
            const confirmedMember = await confirmMonthlyToAnnualUpgrade(mainApi, data.sourceSubscriptionId);
            if (!currentMember && confirmedMember?.data) {
                currentMember = confirmedMember?.data;
            }
            dispatch({ type: FETCH_AUTHENTICATED_USER, payload: { data: { ...currentMember } } });
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS });
            return { isSubscribed: true, currentMember, amountPaid, currency };
        }

        // Scenario 2: BIN Country Mismatch
        if (!isMemberSubscribed && data.annualPlan) {
            if (onBinCountryMismatch) {
                onBinCountryMismatch(data.annualPlan, 'We have changed the billing details to match your billing country, please try again.');
            }
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS });
            return { isSubscribed: false, annualPlan: data.annualPlan };
        }

        // Scenario 1: Happy Path
        if (isMemberSubscribed) {
            dispatch({ type: FETCH_AUTHENTICATED_USER, payload: { data: { ...currentMember } } });
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_SUCCESS });
            return { isSubscribed: true, currentMember, amountPaid, currency };
        }

        // Handle any unexpected error in response
        if (data.error) {
            dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data });
            return { isSubscribed: false, error: data.error };
        }

        // Silent failure guard (mirrors TMS-1922 fix in submitMemberPowerCheckout):
        // a declined card can return 200 with isSubscribed: false and no other
        // failure indicator. Without this branch the consumer would advance the
        // user with no error. If the API supplied a friendly message in the
        // body, compose it onto the default so the user sees the decline reason.
        // Otherwise fall back to an actionable message for the empty-response
        // case (observed in test env for past-due monthly → annual upgrades).
        const friendlyMessage = extractFriendlyMessage(data);
        const errorMessage = friendlyMessage
            ? composeCheckoutErrorMessage(paymentsConstants.paymentErrors.checkout_fail, friendlyMessage)
            : paymentsConstants.paymentErrors.checkout_fail_no_response;
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: errorMessage } });
        return { isSubscribed: false, error: errorMessage };
    } catch (err) {
        const errText = getCheckoutErrorMessage(err, paymentsConstants.paymentErrors.checkout_fail);
        dispatch({ type: SUBSCRIBE_MEMBER_CHECKOUT_FAILURE, data: { error: errText } });
        return { isSubscribed: false, error: errText };
    }
};

export const submitMemberCancel = (subscriptionId, cancellationInfo) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_MEMBER_CANCEL_REQUEST });
        const res = await memberCancel(mainApi, subscriptionId, cancellationInfo);
        const { data } = res;
        if (data.isError) {
            dispatch({ type: SUBSCRIBE_MEMBER_CANCEL_FAILURE });
        } else {
            dispatch({ type: SUBSCRIBE_MEMBER_CANCEL_SUCCESS, data });
        }
    } catch (err) {
        const errText = err.response ? err.response.data : 'Unable to complete subscription cancel';
        dispatch({ type: SUBSCRIBE_MEMBER_CANCEL_FAILURE, message: errText });
    }
};

export const submitMemberRenew = () => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_MEMBER_RENEW_REQUEST });
        const res = await memberRenew(mainApi);
        const { data } = res;
        if (data.isError) {
            gtag('event', {
                category: 'Power Upgrade',
                context: 'Power Upgrade Unsuccessful',
            });
            dispatch({ type: SUBSCRIBE_MEMBER_RENEW_FAILURE });
        } else {
            dispatch({ type: SUBSCRIBE_MEMBER_RENEW_SUCCESS, data });
        }
    } catch (err) {
        const errText = err.response ? err.response.data : 'Unable to complete subscription renewal';
        dispatch({ type: SUBSCRIBE_MEMBER_RENEW_FAILURE, message: errText });
    }
};

export const submitClubRenew = (clubId) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_CLUB_RENEW_REQUEST });
        const res = await clubRenew(mainApi, clubId);
        const { data } = res;
        if (data.isError) {
            dispatch({ type: SUBSCRIBE_CLUB_RENEW_FAILURE });
        } else {
            dispatch({ type: SUBSCRIBE_CLUB_RENEW_SUCCESS, data });
        }
    } catch (err) {
        const errText = err.response ? err.response.data : 'Unable to complete subscription renewal';
        dispatch({ type: SUBSCRIBE_CLUB_RENEW_FAILURE, message: errText });
    }
};

// 23h incomplete_expired webhook is the safety net; a fail-call error must
// not swallow the original Stripe failure surfaced to the user.
const safelyFailClubCheckout = async (mainApi, clubId, sourceSubscriptionId) => {
    try {
        await failClubCheckout(mainApi, clubId, sourceSubscriptionId);
    } catch (failErr) {
        // intentionally swallowed
    }
};

export const submitClubCheckout = ({ values, stripe }) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_REQUEST });
        const res = await clubCheckout(mainApi, values);
        const { data } = res;

        if (data.isError || data.error) {
            dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_FAILURE, data });
            return;
        }

        if (data.paymentIntentClientSecret) {
            const failure = await confirm3dsPayment(stripe, data.paymentIntentClientSecret);
            if (failure) {
                await safelyFailClubCheckout(mainApi, values.clubId, data.sourceSubscriptionId);
                dispatch({
                    type: SUBSCRIBE_CLUB_CHECKOUT_FAILURE,
                    data: { error: failure.errorMessage },
                });
                return;
            }

            const confirmed = await confirmClubCheckout(mainApi, values.clubId, data.sourceSubscriptionId);
            dispatch({ type: FETCH_AUTHENTICATED_USER, payload: confirmed });
            dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_SUCCESS, data: confirmed.data });
            return;
        }

        // Non-3DS happy path — preserve today's 3s debounce on the auth refetch
        setTimeout(() => {
            dispatch({ type: FETCH_AUTHENTICATED_USER, payload: res });
        }, 3000);
        dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_SUCCESS, data });
    } catch (err) {
        const errText = getCheckoutErrorMessage(err, paymentsConstants.paymentErrors.checkout_fail);
        dispatch({
            type: SUBSCRIBE_CLUB_CHECKOUT_FAILURE,
            data: { error: errText },
        });
    }
};

// 23h incomplete_expired webhook is the safety net; a fail-call error must
// not swallow the original Stripe failure surfaced to the user.
const safelyFailEnterpriseCheckout = async (mainApi, clubId, sourceSubscriptionId) => {
    try {
        await failEnterpriseClubCheckout(mainApi, clubId, sourceSubscriptionId);
    } catch (failErr) {
        // intentionally swallowed
    }
};

export const submitEnterpriseClubCheckout = ({ values, stripe }) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_REQUEST });
        const res = await enterpriseClubCheckout(mainApi, values);
        const { data } = res;

        if (data.isError || data.error) {
            dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_FAILURE, data });
            return;
        }

        if (data.paymentIntentClientSecret) {
            const failure = await confirm3dsPayment(stripe, data.paymentIntentClientSecret);
            if (failure) {
                await safelyFailEnterpriseCheckout(mainApi, values.clubId, data.sourceSubscriptionId);
                dispatch({
                    type: SUBSCRIBE_CLUB_CHECKOUT_FAILURE,
                    data: { error: failure.errorMessage },
                });
                return;
            }

            const confirmed = await confirmEnterpriseClubCheckout(
                mainApi, values.clubId, data.sourceSubscriptionId, values.inviteToken,
            );
            dispatch({ type: FETCH_AUTHENTICATED_USER, payload: confirmed });
            dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_SUCCESS, data: confirmed.data });
            return;
        }

        dispatch({ type: FETCH_AUTHENTICATED_USER, payload: res });
        dispatch({ type: SUBSCRIBE_CLUB_CHECKOUT_SUCCESS, data });
    } catch (err) {
        const errText = getCheckoutErrorMessage(err, paymentsConstants.paymentErrors.checkout_fail);
        dispatch({
            type: SUBSCRIBE_CLUB_CHECKOUT_FAILURE,
            data: { error: errText },
        });
    }
};

export const getSubscriptionInfo = () => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_SUBSCRIPTION_INFO_REQUEST });
        const res = await subscriptionInfo(mainApi);
        const { data } = res;
        if (data.isError) {
            dispatch({ type: SUBSCRIBE_SUBSCRIPTION_INFO_FAILURE });
        } else {
            dispatch({ type: SUBSCRIBE_SUBSCRIPTION_INFO_SUCCESS, data });
        }
    } catch (err) {
        const errText = err.response ? err.response.data : 'Unable to get subscription data';
        dispatch({ type: SUBSCRIBE_SUBSCRIPTION_INFO_FAILURE, message: errText });
    }
};

export const updatePaymentDetails = (values) => async (dispatch, getState, { mainApi }) => {
    try {
        dispatch({ type: SUBSCRIBE_UPDATE_PAYMENT_REQUEST });
        const res = await updatePayment(mainApi, values);
        const { data } = res;
        if (data.isError) {
            dispatch({ type: SUBSCRIBE_UPDATE_PAYMENT_FAILURE });
        } else {
            // Include subscriptionId to optimistically clear isPastDue
            dispatch({ type: SUBSCRIBE_UPDATE_PAYMENT_SUCCESS, data, subscriptionId: values.subscriptionId });
        }
    } catch (err) {
        const errText = getCheckoutErrorMessage(err, 'Unable to update payment details');
        dispatch({ type: SUBSCRIBE_UPDATE_PAYMENT_FAILURE, data: { error: errText } });
        throw err;
    }
};

export const printInvoice = (invoice) => async (dispatch) => {
    dispatch({ type: SUBSCRIBE_INVOICE_PRINT, invoice });
};

export const clearCardError = () => async (dispatch) => {
    dispatch({ type: SUBSCRIBE_MEMBER_CLEAR_ERROR });
};
